import { ModularSofa, Raw } from '@fixtuur/backpack';
import * as Hooks from 'preact/hooks';
import { Modify } from './useModularConfiguration';
import { useDispatch } from '../../store/redux';
import * as Actions from '../../store/actions/app';

export type InsertMode = { mode: 'replace' | 'insert'; position: number };

export type Interaction = {
    kind: 'insert' | 'remove';
    identifier: string;
    isActive?: boolean;
    position: Raw.Position3D.Position3D;
    onClick: () => void;
};

export const useModuleInteraction = (
    placedPieces: ModularSofa.PlacedPiece[],
    canInsertAt: (position: number) => boolean,
    checkValid: (modification: Modify) => boolean,
    modify: (modification: Modify) => void,
) => {

    const dispatch = useDispatch();

    const [selectedPiece, setSelectedPiece] = Hooks.useState<number | null>(null);
    type InsertState = { mode: 'replace' } | { mode: 'insert'; index: number };
    const [insertState, setInsertState] = Hooks.useState<InsertState | null>(null);

    const insertMode: InsertMode | null = Hooks.useMemo(() => {
        switch (insertState?.mode) {
            case 'insert':
                return { mode: 'insert', position: insertState.index };
            case 'replace':
                return selectedPiece !== null ? { mode: 'replace', position: selectedPiece } : null;
            default:
                return null;
        }
    }, [insertState, selectedPiece]);

    const selectPiece = Hooks.useCallback((position: number) => {
        setSelectedPiece(position);
        dispatch(Actions.selectPieceAction({
            selectedPiecePosition: position
        }));
        setInsertState({ mode: 'replace' });
    }, []);

    const deselectPiece = Hooks.useCallback(() => {
        setSelectedPiece(null);
        setInsertState(null);
    }, []);

    const insertLeft = Hooks.useCallback(() => {
        setInsertState({ mode: 'insert', index: selectedPiece ?? 0 });
    }, [selectedPiece]);

    const insertRight = Hooks.useCallback(() => {
        setInsertState({
            mode: 'insert',
            index: selectedPiece !== null ? selectedPiece + 1 : placedPieces.length ?? 0,
        });
    }, [selectedPiece, placedPieces.length]);

    const pieceUpdate = Hooks.useMemo(() => {
        if (insertMode === null) return null;
        const modification = (piece: string) => ({
            action: insertMode.mode,
            position: insertMode.position,
            piece,
        });

        const apply = (piece: string) => {
            modify(modification(piece));
            if (insertMode.mode === 'replace') {
                dispatch(Actions.changePieceAction({
                    newPiece: piece,
                    newPiecePosition: insertMode.position
                }));
            }
            if (insertMode.mode === 'insert') {
                dispatch(Actions.addPieceAction({
                    addedPiece: piece,
                    addedPiecePosition: insertMode.position
                }));
            }
            selectPiece(insertMode.position);
        };

        return {
            replacePiece: insertMode.mode === 'replace' ? insertMode.position : null,
            modification,
            apply,
        };
    }, [insertMode]);

    const interactions: Array<Interaction> = Hooks.useMemo(() => {
        if (placedPieces.length === 0) return [];

        const interactions: Array<Interaction> = [];

        const leftInsertPosition = selectedPiece ?? 0;
        if (canInsertAt(leftInsertPosition)) {
            const [lx, ly, lz] = placedPieces[leftInsertPosition].leftActionPosition.value();
            interactions.push({
                kind: 'insert',
                isActive: pieceUpdate !== null && insertMode?.mode === 'insert' && insertMode.position === leftInsertPosition,
                identifier: 'left-add',
                position: { x: lx, y: ly, z: lz },
                onClick: () => {
                    dispatch(Actions.addButtonClickAction({
                        selectedPiecePosition: selectedPiece,
                        buttonSide: 'left'
                    }));
                    insertLeft();
                },
            });
        }

        const rightInsertPosition =
            selectedPiece !== null ? selectedPiece + 1 : placedPieces.length;

        if (canInsertAt(rightInsertPosition)) {
            const [rx, ry, rz] = placedPieces[rightInsertPosition - 1].rightActionPosition.value();
            interactions.push({
                kind: 'insert',
                isActive: pieceUpdate !== null && insertMode?.mode === 'insert' && insertMode.position === rightInsertPosition,
                identifier: 'right-add',
                position: { x: rx, y: ry, z: rz },
                onClick: () => {
                    dispatch(Actions.addButtonClickAction({
                        selectedPiecePosition: selectedPiece,
                        buttonSide: 'right'
                    }));
                    insertRight();
                },
            });
        }

        if (selectedPiece !== null) {
            const modification: Modify = { action: 'remove', position: selectedPiece };
            if (checkValid(modification)) {
                const [tx, ty, tz] = placedPieces[selectedPiece].topActionPosition.value();
                interactions.push({
                    kind: 'remove',
                    identifier: 'center-remove',
                    position: { x: tx, y: ty, z: tz },
                    onClick: () => {
                        modify(modification);
                        dispatch(Actions.removePieceAction({
                            removedPiecePosition: modification.position
                        }));
                        deselectPiece();
                    },
                });
            }
        }

        return interactions;
    }, [placedPieces, selectedPiece, canInsertAt, checkValid, insertLeft, insertRight, insertMode]);

    return { selectedPiece, selectPiece, deselectPiece, interactions, pieceUpdate };
};
